import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { alpacaService, Order, StrategistStats, StrategistDecision } from '../../services/AlpacaService';
import { useAccountMode } from '../../context/AccountModeContext';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import { AggregateStats, EMPTY_AGGREGATE, computeAggregateStats } from '../../utils/tradeUtils';
import styles from './Leaderboard.module.css';

type TimePeriod = 'all' | 'month' | 'week';

/* ── AI stats from strategist decisions ── */
function computeAIStatsFromDecisions(decisions: StrategistDecision[]): AggregateStats {
  const closed = decisions.filter((d) => d.status === 'closed_profit' || d.status === 'closed_loss');
  if (closed.length === 0) return EMPTY_AGGREGATE;

  let wins = 0,
    losses = 0,
    totalWinAmt = 0,
    totalLossAmt = 0;
  let totalPnl = 0,
    bestPct = -Infinity,
    worstPct = Infinity;
  const pnlPcts: number[] = [];

  for (const d of closed) {
    const pnl = d.pnl ?? 0;
    const pnlPct = d.pnlPct ?? 0;
    totalPnl += pnl;
    pnlPcts.push(pnlPct);
    if (pnlPct > bestPct) bestPct = pnlPct;
    if (pnlPct < worstPct) worstPct = pnlPct;
    if (pnl > 0) {
      wins++;
      totalWinAmt += pnl;
    } else {
      losses++;
      totalLossAmt += Math.abs(pnl);
    }
  }

  const total = wins + losses;
  return {
    totalTrades: total,
    winRate: total > 0 ? (wins / total) * 100 : 0,
    avgPnlPct: pnlPcts.length > 0 ? pnlPcts.reduce((a, b) => a + b, 0) / pnlPcts.length : 0,
    bestTradePct: bestPct === -Infinity ? 0 : bestPct,
    worstTradePct: worstPct === Infinity ? 0 : worstPct,
    profitFactor: totalLossAmt > 0 ? totalWinAmt / totalLossAmt : totalWinAmt > 0 ? Infinity : 0,
    totalPnlDollars: totalPnl,
  };
}

/* ── Metric comparison ── */
interface MetricDef {
  key: keyof AggregateStats;
  label: string;
  format: (v: number) => string;
  higherWins: boolean;
}

const METRICS: MetricDef[] = [
  { key: 'winRate', label: 'Win Rate', format: (v) => `${v.toFixed(1)}%`, higherWins: true },
  { key: 'avgPnlPct', label: 'Avg Trade P&L', format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, higherWins: true },
  { key: 'bestTradePct', label: 'Best Trade', format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, higherWins: true },
  {
    key: 'worstTradePct',
    label: 'Worst Trade',
    format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`,
    higherWins: false,
  },
  { key: 'totalTrades', label: 'Total Trades', format: (v) => String(v), higherWins: true },
  {
    key: 'profitFactor',
    label: 'Profit Factor',
    format: (v) => (v === Infinity ? '∞' : v.toFixed(2)),
    higherWins: true,
  },
];

function getWinner(ai: number, human: number, higherWins: boolean): 'ai' | 'human' | 'tie' {
  if (ai === human) return 'tie';
  if (higherWins) return ai > human ? 'ai' : 'human';
  // For "worst trade" — less negative is better
  return ai > human ? 'ai' : 'human';
}

function getCutoffDate(period: TimePeriod): Date | null {
  if (period === 'all') return null;
  const now = new Date();
  if (period === 'week') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
}

const Leaderboard: React.FC = () => {
  const { mode } = useAccountMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState<TimePeriod>('all');

  // Raw data
  const [allDecisions, setAllDecisions] = useState<StrategistDecision[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError('');
      try {
        const [decisionsRes, ordersRes] = await Promise.all([
          alpacaService.getStrategistDecisions(undefined, 500),
          alpacaService.getOrders('all', '500', undefined, undefined, mode),
        ]);
        setAllDecisions(decisionsRes.decisions || []);
        setAllOrders((ordersRes as any).orders || (ordersRes as any) || []);
      } catch (e: any) {
        setError(e.message || 'Failed to load leaderboard data');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [mode]);

  // Filter by period and compute stats
  const { aiStats, humanStats } = useMemo(() => {
    const cutoff = getCutoffDate(period);

    const filteredDecisions = cutoff ? allDecisions.filter((d) => new Date(d.createdAt) >= cutoff) : allDecisions;

    const filteredOrders = cutoff ? allOrders.filter((o) => new Date(o.submitted_at) >= cutoff) : allOrders;

    return {
      aiStats: computeAIStatsFromDecisions(filteredDecisions),
      humanStats: computeAggregateStats(filteredOrders),
    };
  }, [allDecisions, allOrders, period]);

  // Score tally
  const scores = useMemo(() => {
    let ai = 0,
      human = 0;
    if (aiStats.totalTrades === 0 && humanStats.totalTrades === 0) return { ai: 0, human: 0 };

    for (const m of METRICS) {
      const winner = getWinner(aiStats[m.key] as number, humanStats[m.key] as number, m.higherWins);
      if (winner === 'ai') ai++;
      else if (winner === 'human') human++;
    }
    return { ai, human };
  }, [aiStats, humanStats]);

  const overallWinner = scores.ai > scores.human ? 'ai' : scores.human > scores.ai ? 'human' : 'tie';

  if (loading)
    return (
      <div className={styles.page}>
        <SignalLoader />
      </div>
    );
  if (error)
    return (
      <div className={styles.page}>
        <p className={styles.error}>{error}</p>
      </div>
    );

  const bothEmpty = aiStats.totalTrades === 0 && humanStats.totalTrades === 0;

  return (
    <section className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Strategist vs You</h1>
        <p className={styles.subtitle}>Head-to-head: how does the AI stack up against your manual trades?</p>
      </div>

      {/* Time period pills */}
      <div className={styles.periodRow}>
        {(['all', 'month', 'week'] as TimePeriod[]).map((p) => (
          <button
            key={p}
            className={`${styles.periodPill} ${period === p ? styles.periodActive : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p === 'all' ? 'All Time' : p === 'month' ? 'This Month' : 'This Week'}
          </button>
        ))}
      </div>

      {bothEmpty ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>No trades yet!</p>
          <p className={styles.emptyText}>
            Start trading via <Link to="/quick-trade">Quick Trade</Link> and enable the{' '}
            <Link to="/strategist">Strategist</Link> to see who trades better.
          </p>
        </div>
      ) : (
        <>
          {/* Score Hero */}
          <div className={styles.scoreHero}>
            <div className={`${styles.contestant} ${overallWinner === 'ai' ? styles.winner : ''}`}>
              <span className={styles.contestantIcon}>🤖</span>
              <span className={styles.contestantName}>Strategist</span>
              <span className={styles.score}>{scores.ai}</span>
              {overallWinner === 'ai' && <span className={styles.crown}>👑</span>}
            </div>
            <div className={styles.vsCenter}>
              <span className={styles.vsText}>VS</span>
              <span className={styles.vsSubtext}>{aiStats.totalTrades + humanStats.totalTrades} trades compared</span>
            </div>
            <div className={`${styles.contestant} ${overallWinner === 'human' ? styles.winner : ''}`}>
              <span className={styles.contestantIcon}>🧑‍💻</span>
              <span className={styles.contestantName}>You</span>
              <span className={styles.score}>{scores.human}</span>
              {overallWinner === 'human' && <span className={styles.crown}>👑</span>}
            </div>
          </div>

          {/* Metric Battle Grid */}
          <div className={styles.metricGrid}>
            {METRICS.map((m) => {
              const aiVal = aiStats[m.key] as number;
              const humanVal = humanStats[m.key] as number;
              const winner = getWinner(aiVal, humanVal, m.higherWins);
              // Tug bar proportions
              const absAi = Math.abs(aiVal);
              const absHuman = Math.abs(humanVal);
              const total = absAi + absHuman;
              const aiPct = total > 0 ? (absAi / total) * 100 : 50;

              return (
                <div key={m.key} className={styles.metricCard}>
                  <span className={styles.metricLabel}>{m.label}</span>
                  <div className={styles.metricBattle}>
                    <div className={`${styles.metricSide} ${winner === 'ai' ? styles.metricWinner : ''}`}>
                      <span className={styles.metricPlayer}>AI</span>
                      <span className={styles.metricValue}>{m.format(aiVal)}</span>
                      {winner === 'ai' && <span className={styles.trophy}>★</span>}
                    </div>
                    <div className={`${styles.metricSide} ${winner === 'human' ? styles.metricWinner : ''}`}>
                      <span className={styles.metricPlayer}>You</span>
                      <span className={styles.metricValue}>{m.format(humanVal)}</span>
                      {winner === 'human' && <span className={styles.trophy}>★</span>}
                    </div>
                  </div>
                  <div className={styles.tugBar}>
                    <div className={styles.tugAi} style={{ width: `${aiPct}%` }} />
                    <div className={styles.tugHuman} style={{ width: `${100 - aiPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* P&L Summary */}
          <div className={styles.pnlSummary}>
            <div className={styles.pnlCard}>
              <span className={styles.pnlLabel}>AI Total P&L</span>
              <span
                className={`${styles.pnlValue} ${aiStats.totalPnlDollars >= 0 ? styles.positive : styles.negative}`}
              >
                ${aiStats.totalPnlDollars >= 0 ? '+' : ''}
                {aiStats.totalPnlDollars.toFixed(2)}
              </span>
            </div>
            <div className={styles.pnlCard}>
              <span className={styles.pnlLabel}>Your Total P&L</span>
              <span
                className={`${styles.pnlValue} ${humanStats.totalPnlDollars >= 0 ? styles.positive : styles.negative}`}
              >
                ${humanStats.totalPnlDollars >= 0 ? '+' : ''}
                {humanStats.totalPnlDollars.toFixed(2)}
              </span>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default Leaderboard;
